import { useMemo } from 'react';
import {
  Container,
  DesignTokenSizeEnum,
  styled,
  Text,
  TextInput,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { Info, Magnifier } from '@volkswagen-onehub/icons-core';
import Box from '@/modules/components/Box/Box';
import FullScreenOverlay from '@/modules/components/FullScreenOverlay/FullScreenOverlay';
import SectionHeader from '@/modules/components/SectionHeader/SectionHeader';
import {
  getCategorySpecs,
  getReplacedSpecData,
  isWheelsFeature,
} from '@/modules/utils/storeUtils';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { StyledOverlay } from './FeatureOverlay.styles';
import CTAModal from '@/modules/components/CTAModal/CTAModal';
import TyreInformation from '@/modules/features/components/TyreInformation/TyreInformation';
import Accordion from '@/modules/components/Accordion/Accordion';
import OverlayHeader from '@//modules/components/FullScreenOverlay/OverlayHeader';
import { SpecItem } from '@tribal-worldwide-london/fa-configuration-service-vwg';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import {
  performVWBasicLayerClose,
  performVWBasicLayerLoad,
} from '@/analytics/events';
import { FeatureStatus } from '@/hooks/useFeatures';
import { SpecOption } from '@tribal-worldwide-london/fa-product-service-vwg';
import { StockSpecOption } from '@tribal-worldwide-london/fa-stock-service-vwg';
import { useSpecSearch } from '@/hooks/useSpecSearch';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { isMobile } from '@/modules/utils/breakpoints';

export const LineThroughSpan = styled.span`
  text-decoration: line-through;
`;

interface FeatureOverlayProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  handleClose(): void;
  sectionName?: SectionNames;
}

const FeatureOverlay = ({
  activeConfiguration,
  handleClose,
  sectionName,
}: FeatureOverlayProps) => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { retailers } = useGlobalStore((state) => state.app);  
  const isMobileWidth = useResizeResponsive(isMobile);
  const specItems =
    activeConfiguration.configData?.specItems ||
    activeConfiguration.stockData?.specItems ||
    [];
  const replacedStandardSpecs =
    activeConfiguration.configData?.replacedStandardSpecs ||
    activeConfiguration.stockData?.replacedStandardSpecs ||
    [];
  const specOptions =
    activeConfiguration.marketingShortCode
      ? activeConfiguration?.productData?.options 
      : activeConfiguration?.stockData?.options;

  const replacedSpecsData = 
    replacedStandardSpecs.map((replacedStandardSpecId: string) => {
      const replacedSpecData = getReplacedSpecData(replacedStandardSpecId, specOptions);
      return replacedSpecData;
    });

  const isStock = Boolean(
    activeConfiguration.vin || activeConfiguration.orderNumber
  );
  const categorySpecs: { [key: string]: SpecItem[] } = getCategorySpecs(
    [...specItems, ...replacedSpecsData],
    isStock
  );

  const getModalProps = (feature: any) => {
    const modalProps = {
      title: feature.title,
      description:
        feature.title !== feature.description ? feature.description : null,
      shortDescription: feature.shortDescription ? feature.shortDescription : null,
    };
    if (isWheelsFeature(feature))
      return {
        ...modalProps,
        body: <TyreInformation configuration={activeConfiguration} />,
      };

    return modalProps;
  };

  const analyticsCommonFields = {
    sectionName,
    configuration: activeConfiguration,
    retailers,
    analyticsBuilder,
    analyticsManager,
  };

  const categories = useMemo(
    () => Object.entries(categorySpecs),
    [categorySpecs]
  );

  const {
    searchRef,
    error,
    isSpecDetailOpen,
    renderTextWithHighlights,
    handleSubmitSearch,
    handleClearSearch,
    handleToggleAccordion,
  } = useSpecSearch({
    categories,
    mode: 'single',
    analyticsCommonFields,
  });

  return (
    <FullScreenOverlay
      header={<OverlayHeader handleClose={handleClose} headerBorder={false} />}
      openCallback={() => performVWBasicLayerLoad(analyticsCommonFields)}
      closeCallback={() => performVWBasicLayerClose(analyticsCommonFields)}
      gradient
    >
      <StyledOverlay>
        <Box mb={DesignTokenSizeEnum.dynamic0100}>
          <Container
            horizontalAlign="space-between"
            verticalAlign="flex-end"
            gutter={DesignTokenSizeEnum.dynamic0100}
            stretchContent
            wrap="auto"
          >
            <Container wrap="always">
              <Box mb={DesignTokenSizeEnum.dynamic0020}>
                <SectionHeader
                  title={contentManager.get('standard-feature-label')}
                  titleBold={contentManager.get(
                    'key-features-section-title-bold'
                  )}
                  titleSize={TokenTextAppearance.headline400}
                />
              </Box>
              <Box
                as={Text}
                appearance={TokenTextAppearance.copy200}
                mt={DesignTokenSizeEnum.dynamic0020}
              >
                {contentManager.get('feature-overlay-subtitle')}
              </Box>
            </Container>

            <Container
              wrap={isMobileWidth ? 'always' : 'auto'}
              horizontalAlign="flex-end"
              verticalAlign="flex-start"
            >
              <Box width={isMobileWidth ? '100%' : '340px'} height={'54px'}>
                <form onSubmit={handleSubmitSearch}>
                  <TextInput
                    label="Search"
                    isFloating
                    type="search"
                    ref={searchRef}
                    hasClearButton
                    isClearButtonVisible
                    onClear={handleClearSearch}
                    icon={<Magnifier />}
                    appearance={error ? 'error' : 'default'}
                    message="No results found"
                  />
                </form>
              </Box>
            </Container>
          </Container>
        </Box>

        {categories.map(([key, value], index) => (
          <Accordion
            key={key}
            title={
              <Text appearance={TokenTextAppearance.bigcopy100}>
                {renderTextWithHighlights(key)}
              </Text>
            }
            id={key}
            isOpen={isSpecDetailOpen[index]}
            onToggle={() => handleToggleAccordion(index, key, true)}
          >
            <ul>
              {value.map((feature: SpecItem | SpecOption | StockSpecOption) => (
                <li key={feature.title}>
                  <Box
                    as={Text}
                    appearance={TokenTextAppearance.copy200}
                    my={DesignTokenSizeEnum.dynamic0020}
                  >
                    {
                      feature.availability === FeatureStatus.REPLACED
                        ? <><LineThroughSpan>{renderTextWithHighlights(feature.title)}</LineThroughSpan> - {contentManager.get('replaced-text')}</>
                        : renderTextWithHighlights(feature.title)
                    }
                    {(feature.title !== feature.description ||
                      isWheelsFeature(feature)) && (
                      <>
                        {' '}
                        <CTAModal
                          tag="button"
                          modalProps={getModalProps(feature)}
                          openCallback={() =>
                            performVWBasicLayerLoad(analyticsCommonFields)
                          }
                          closeCallback={() =>
                            performVWBasicLayerClose(analyticsCommonFields)
                          }
                        >
                          <Info variant="small" />
                        </CTAModal>
                      </>
                    )}
                  </Box>
                </li>
              ))}
            </ul>
          </Accordion>
        ))}
      </StyledOverlay>
    </FullScreenOverlay>
  );
};

export default FeatureOverlay;
